import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError, coverUrl } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import TaxonomyFilterBar, { filtersToParams } from "../components/TaxonomyFilterBar";
import { Play, BookOpenText, Clock } from "lucide-react";

const DEFAULT_COVER = "https://images.unsplash.com/photo-1650233323933-53e20427e277?crop=entropy&cs=srgb&fm=jpg&w=400&q=80";

function SermonRow({ item, list, index }) {
  const { playQueue, currentTrack, isPlaying } = usePlayer();
  const isCurrent = currentTrack?.id === item.id;
  const cover = coverUrl(item) || DEFAULT_COVER;
  return (
    <button
      onClick={() => playQueue(list, index, "normal")}
      data-testid={`sermon-row-${item.id}`}
      className="w-full flex items-center gap-4 p-3 rounded-md hover:bg-[#111317] border border-transparent hover:border-[#22262E] transition-all duration-300 text-left"
    >
      <div className="relative w-14 h-14 rounded-md overflow-hidden border border-[#22262E] bg-[#111317] flex-shrink-0">
        <img src={cover} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex items-center justify-center bg-[#060709]/60 opacity-0 hover:opacity-100 transition-opacity">
          <Play className="w-5 h-5 text-[#C5A059]" strokeWidth={2} />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-medium truncate ${isCurrent ? "text-[#C5A059]" : "text-[#FDFBF7]"}`}>{item.title}</div>
        <div className="text-[#A1A4AB] text-sm truncate">{item.artist}</div>
        {item.scripture_theme && <div className="overline text-[#6C707A] mt-0.5 truncate">{item.scripture_theme}</div>}
      </div>
      <div className="hidden md:block text-right">
        <div className="overline text-[#C5A059]">{item.category}</div>
        {isCurrent && isPlaying && <div className="text-xs text-[#A1A4AB] flex items-center gap-1 justify-end mt-1"><Clock className="w-3 h-3" strokeWidth={1.5} /> playing</div>}
      </div>
    </button>
  );
}

export default function SermonsLibrary() {
  const [sermons, setSermons] = useState([]);
  const [teachings, setTeachings] = useState([]);
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const baseParams = { ...filtersToParams(filters) };
      if (query.trim()) baseParams.search = query.trim();
      const [s, t, st] = await Promise.all([
        api.get("/tracks", { params: { ...baseParams, category: "sermon" } }),
        api.get("/tracks", { params: { ...baseParams, category: "teaching" } }),
        api.get("/tracks", { params: { ...baseParams, category: "study" } }),
      ]);
      setSermons(s.data || []);
      setTeachings(t.data || []);
      setStudies(st.data || []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters, query]);

  useEffect(() => { load(); }, [load]);

  const combined = [...sermons, ...teachings, ...studies];

  return (
    <div className="space-y-10">
      <div>
        <div className="overline text-[#C5A059] mb-2">The Word & the Voice</div>
        <h1 className="font-serif text-[#FDFBF7] text-4xl sm:text-5xl tracking-tight">Sermons & Teachings</h1>
        <p className="text-[#A1A4AB] mt-2">Study, teaching, and sermons — submitted and reviewed for the station.</p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="text-[#A1A4AB] text-sm">Filter by genre, mood, energy or recency.</div>
        <input
          placeholder="Search title, speaker, theme..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="input-lux md:max-w-xs"
          data-testid="sermons-search-input"
        />
      </div>

      <TaxonomyFilterBar value={filters} onChange={setFilters} testIdPrefix="sermons-tax-filter" />

      {err && <div className="text-red-400 text-sm" data-testid="sermons-error">{err}</div>}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-20 rounded-md shimmer" />
          ))}
        </div>
      ) : combined.length === 0 ? (
        <div className="card-surface p-16 text-center" data-testid="sermons-empty-state">
          <BookOpenText className="w-8 h-8 text-[#C5A059] mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-[#FDFBF7] font-serif text-2xl mb-2">No teachings match your filters</div>
          <div className="text-[#A1A4AB]">Try clearing filters, or submit a sermon for review to start filling this sanctuary.</div>
        </div>
      ) : (
        <div className="space-y-12">
          {sermons.length > 0 && (
            <section>
              <h2 className="font-serif text-[#FDFBF7] text-2xl md:text-3xl tracking-tight mb-4">Sermons</h2>
              <div className="card-surface divide-y divide-[#22262E]/60">
                {sermons.map((s, i) => <SermonRow key={s.id} item={s} list={sermons} index={i} />)}
              </div>
            </section>
          )}
          {teachings.length > 0 && (
            <section>
              <h2 className="font-serif text-[#FDFBF7] text-2xl md:text-3xl tracking-tight mb-4">Teachings</h2>
              <div className="card-surface divide-y divide-[#22262E]/60">
                {teachings.map((s, i) => <SermonRow key={s.id} item={s} list={teachings} index={i} />)}
              </div>
            </section>
          )}
          {studies.length > 0 && (
            <section>
              <h2 className="font-serif text-[#FDFBF7] text-2xl md:text-3xl tracking-tight mb-4">Study songs</h2>
              <div className="card-surface divide-y divide-[#22262E]/60">
                {studies.map((s, i) => <SermonRow key={s.id} item={s} list={studies} index={i} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
